import { Link, useParams } from "react-router";
import { Alerta, Adjuntos, Badge, Button, Card, Combobox, DataList, Desplegable, Dialog, MontoField, PercentField, Row, Select, SelectorArchivos, Spinner, Stack, TextField } from "@/components";
import { ACCEPT_ADJUNTO } from "@/lib/adjuntos";
import type { Pago } from "@/lib/api";
import { formatFecha, formatMoneda, formatNumero } from "@/lib/format";
import { esDeLaCompraDelTerreno, ETIQUETA_ESTADO, RUTA_COMPRA_TERRENO, montosConFee, nombreProveedor, sinFee, TEXTO_SIN_FEE, textoFormaPago, textoMedio, TONO_ESTADO, type LineaAsignacion } from "@/lib/gastos";
import { textoPresupuesto } from "@/lib/facturas";
import { FormularioGasto } from "./FormularioGasto";
import { useGastoTab } from "./useGastoTab";

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="contents">
      <dt className="text-texto-suave">{etiqueta}</dt>
      <dd className="break-words">{valor}</dd>
    </div>
  );
}

/**
 * `key={id}` fuerza un remount al navegar entre gastos (ej. "Ver fee"/"Ver gasto origen"): sin esto,
 * `useApiResource` no limpia `data` al cambiar de ruta y la ficha vieja queda en pantalla (con sus diálogos
 * y acciones) mientras el pedido del gasto nuevo todavía está en vuelo.
 */
export function GastoTab() {
  const { id } = useParams();
  return <Ficha key={id} />;
}

function Ficha() {
  const t = useGastoTab();
  const g = t.gasto;
  const d = t.dialogo;

  if (t.cargando) {
    return (
      <div className="grid place-items-center py-10 text-primario">
        <Spinner />
      </div>
    );
  }
  if (t.errorCarga || !g) {
    return (
      <Stack espacio={3}>
        <Alerta texto={t.errorCarga ?? "No existe ese gasto"} />
        <Link to="/gastos" className="text-primario underline-offset-2 hover:underline">
          Volver a gastos
        </Link>
      </Stack>
    );
  }

  const tc = (valor: string | null) => (valor ? formatNumero(valor) : "—");
  // El fee va dentro del gasto (Mati, 2026-10-28): monto, pagado y saldo lo incluyen; admin y contador ven el desglose.
  const m = montosConFee(g);
  const pct = g.fee_porcentaje?.replace(".00", "").replace(".", ",");
  const columnasPagos = [
    { clave: "fecha", titulo: "Fecha", render: (p: Pago) => formatFecha(p.fecha) },
    {
      clave: "monto",
      titulo: "Monto",
      // con fee, el total que se pagó y cómo se partió (Mati, 2026-11-02)
      render: (p: Pago) => {
        const conFee = t.totalesPagos.get(p.id);
        // el fee va entero en el pago que saldó al proveedor (Mati, 2026-11-02): los parciales, tal cual
        if (!conFee || conFee.fee === "0.00") return formatMoneda(p.monto, p.moneda);
        return (
          <Stack espacio={1}>
            <span>{formatMoneda(conFee.total, p.moneda)}</span>
            {!t.sumarFee && <span className="text-chico text-texto-suave">{`Proveedor ${formatMoneda(p.monto, p.moneda)} + fee ${formatMoneda(conFee.fee, p.moneda)}`}</span>}
          </Stack>
        );
      },
    },
    { clave: "tc", titulo: "Tipo de cambio", render: (p: Pago) => tc(p.tipo_cambio) },
    { clave: "usd", titulo: "En USD", render: (p: Pago) => formatMoneda(p.monto_usd, "USD") },
    { clave: "medio", titulo: "Medio", render: (p: Pago) => textoMedio(p) },
    { clave: "nota", titulo: "Nota", render: (p: Pago) => p.nota ?? "—" },
  ];
  const columnasAsignacion = [
    { clave: "nombre", titulo: "Fiduciante o grupo", render: (l: LineaAsignacion) => (l.grupo_id !== null ? `${l.nombre} (grupo)` : l.nombre) },
    {
      clave: "pct",
      titulo: "%",
      render: (l: LineaAsignacion) => <PercentField etiqueta={`% de ${l.nombre}`} valor={l.porcentaje} onCambio={(v) => t.cambiarPorcentajeAsignacion(l.clave, v)} />,
    },
  ];
  const accionesPago = (p: Pago) => (
    <>
      <Button variante="secundario" tamanio="sm" aria-label={`Editar pago del ${formatFecha(p.fecha)}`} onClick={() => t.abrirPago(p)}>
        Editar
      </Button>
      <Button variante="fantasma" tamanio="sm" aria-label={`Borrar pago del ${formatFecha(p.fecha)}`} onClick={() => t.abrirBorrarPago(p)}>
        Borrar
      </Button>
    </>
  );

  return (
    <Stack espacio={6}>
      <Stack espacio={2}>
        <Link to="/gastos" className="text-chico text-primario underline-offset-2 hover:underline">
          ← Gastos
        </Link>
        <Row justificar="entre" envolver espacio={3}>
          <Row espacio={2} envolver>
            <h1 className="text-pantalla font-light tracking-[-0.03em]">{g.descripcion}</h1>
            {g.es_fee && <Badge tono="primario">Fee</Badge>}
            <Badge>{g.tipo}</Badge>
            <Badge tono={TONO_ESTADO[m.estado]}>{ETIQUETA_ESTADO[m.estado]}</Badge>
          </Row>
          {t.esAdmin && (
            <Row espacio={2} alinear="centro" envolver>
              {g.estado !== "pagado" && <Button onClick={() => t.abrirPago(null)}>Registrar pago</Button>}
              {!g.es_fee &&
                (esDeLaCompraDelTerreno(g) ? (
                  <span className="text-chico text-texto-suave">Cargado desde la ficha del terreno: para corregirlo, borralo y cargalo de nuevo ahí.</span>
                ) : (
                  <Button variante="secundario" onClick={t.abrirEditar}>
                    Editar
                  </Button>
                ))}
              {!g.es_fee && (
                <Button variante="fantasma" onClick={t.abrirBorrar}>
                  Borrar
                </Button>
              )}
            </Row>
          )}
        </Row>
      </Stack>

      {g.pago_a_cuenta_terreno && (
        <p className="text-chico">
          Es un pago a cuenta de la compra del terreno.{" "}
          <Link to={RUTA_COMPRA_TERRENO} className="text-primario underline-offset-2 hover:underline">
            Ver la compra del terreno
          </Link>
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-chico">
            <Dato etiqueta="Fecha" valor={formatFecha(g.fecha)} />
            <Dato etiqueta="Monto" valor={sinFee(g) ? `${formatMoneda(m.monto, g.moneda)} · ${TEXTO_SIN_FEE.toLowerCase()}` : formatMoneda(m.monto, g.moneda)} />
            <Dato etiqueta="Pagado" valor={formatMoneda(m.pagado, g.moneda)} />
            <Dato etiqueta="Saldo" valor={formatMoneda(m.saldo, g.moneda)} />
            {!g.es_fee && <Dato etiqueta="Forma de pago" valor={textoFormaPago(g.forma_pago)} />}
            <Dato etiqueta="Rubro" valor={g.rubro.nombre} />
            <Dato etiqueta="Nro de comprobante" valor={g.comprobante_nro ?? "—"} />
            <Dato etiqueta="Tipo de cambio" valor={tc(g.tipo_cambio)} />
            <Dato etiqueta={m.incluyeFee ? "En USD, sin fee" : "En USD"} valor={formatMoneda(g.monto_usd, "USD")} />
            <Dato etiqueta="Proveedor" valor={nombreProveedor(g)} />
            {g.cotizacion && !t.sumarFee && (
              <div className="contents">
                <dt className="text-texto-suave">Presupuesto</dt>
                <dd className="break-words">
                  <Link to={`/cotizaciones/${g.cotizacion.id}`} className="text-primario underline-offset-2 hover:underline">
                    {textoPresupuesto(g)}
                  </Link>
                  {!g.cotizacion.aceptado && <span className="text-texto-suave"> · ya no está aceptado</span>}
                </dd>
              </div>
            )}
            {g.item && <Dato etiqueta="Ítem" valor={g.item.nombre} />}
          </dl>
        </Card>
        {/* El fiduciante ve el fee ya sumado al monto, sin porcentaje ni desglose (Mati, 2026-10-20). */}
        {!t.sumarFee && (
          <Card>
            <Stack espacio={2}>
              <h2 className="text-chico font-medium text-texto-suave">{g.es_fee ? "Fee de administración" : "Desglose"}</h2>
              {g.es_fee && g.gasto_origen_id !== null && (
                <p className="text-chico">
                  Este es el fee de otro gasto.{" "}
                  <Link to={`/gastos/${g.gasto_origen_id}`} className="text-primario underline-offset-2 hover:underline">
                    Ver gasto origen
                  </Link>
                </p>
              )}
              {!g.es_fee && g.fee && (
                <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-chico">
                  <Dato etiqueta="Gasto" valor={formatMoneda(g.monto, g.moneda)} />
                  <Dato etiqueta={`Fee de administración ${pct} %`} valor={formatMoneda(g.fee.monto, g.moneda)} />
                  <div className="contents font-medium">
                    <dt>Total</dt>
                    <dd>{formatMoneda(m.monto, g.moneda)}</dd>
                  </div>
                  <dd className="col-span-2 pt-1 text-texto-suave">
                    {`Fee cobrado ${formatMoneda(g.fee.pagado, g.moneda)} · pendiente ${formatMoneda(g.fee.saldo, g.moneda)} · `}
                    <Link to={`/gastos/${g.fee.id}`} className="text-primario underline-offset-2 hover:underline">
                      Ver pagos del fee
                    </Link>
                  </dd>
                </dl>
              )}
              {!g.es_fee && !g.fee && <p className="text-chico text-texto-suave">Este gasto no lleva fee.</p>}
            </Stack>
          </Card>
        )}
      </div>

      <Alerta tono="alerta" texto={g.adjuntos.length === 0 ? (t.avisoAlta ?? (t.esAdmin && !g.es_fee ? "Falta el comprobante de este gasto: subilo acá abajo." : null)) : null} />
      <Adjuntos etiqueta="Comprobante" adjuntos={g.adjuntos} rutaDescarga={t.rutaDescarga} puedeEditar={t.esAdmin} onSubir={t.subirAlGasto} onBorrar={t.borrarAdjunto} subiendo={t.subiendo} error={t.errorAdjuntos} />

      <Stack espacio={3}>
        <Row justificar="entre" envolver espacio={3}>
          <h2 className="text-titulo font-light tracking-tight">Pagos</h2>
        </Row>
        {g.estado !== "pagado" && (
          <p className="text-texto-suave">{`Falta pagar ${formatMoneda(t.totalAPagar ?? g.saldo, g.moneda)}${g.fee && !g.es_fee ? " con el fee" : ""}. Anotar el gasto no mueve la caja: la plata sale cuando se registra el pago.`}</p>
        )}
        <Alerta texto={t.avisoComprobantes} />
        <Alerta texto={t.errorBorrarPago} />
        <DataList etiqueta="Pagos" columnas={columnasPagos} filas={g.pagos} claveFila={(p) => p.id} acciones={t.esAdmin ? accionesPago : undefined} vacio="Todavía no hay pagos." />
        {g.pagos.map((p) => (
          <Desplegable key={p.id} titulo={`Comprobantes del pago del ${formatFecha(p.fecha)} (${p.adjuntos.length})`}>
            <Adjuntos etiqueta={`Comprobantes del pago del ${formatFecha(p.fecha)}`} adjuntos={p.adjuntos} rutaDescarga={t.rutaDescarga} puedeEditar={t.esAdmin} onSubir={t.subirAlPago(p)} onBorrar={t.borrarAdjunto} subiendo={t.subiendo} error={null} />
          </Desplegable>
        ))}
      </Stack>

      <Dialog abierto={d?.tipo === "editar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Editar gasto">
        <FormularioGasto
          form={t.formG}
          cambiar={t.cambiarG}
          errores={t.erroresG}
          opcionesProveedor={t.opcionesProveedorForm}
          onCrearProveedor={t.crearProveedor}
          opcionesRubro={t.opcionesRubroForm}
          opcionesPresupuesto={t.opcionesPresupuesto}
          opcionesCuota={t.opcionesCuota}
          opcionesItem={t.opcionesItem}
          feePrevisto={t.feePrevisto}
          feePorcentaje={t.feePorcentaje}
          onSubmit={(e) => void t.guardarGasto(e)}
          onCancelar={t.cerrar}
          guardando={t.guardando}
          error={t.errorGuardar}
        />
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar gasto">
        <Stack espacio={4}>
          <p>{t.mensajeBorrado}</p>
          <Alerta texto={t.borrado.error} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.borrado.loading} onClick={() => void t.confirmarBorrar()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>

      <Dialog abierto={d?.tipo === "pago"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={d?.tipo === "pago" && d.pago ? "Editar pago" : "Registrar pago"}>
        <form onSubmit={(e) => void t.guardarPago(e)} noValidate>
          <Stack espacio={4}>
            <TextField etiqueta="Fecha" type="date" value={t.formP.fecha} onChange={(e) => t.cambiarP("fecha", e.target.value)} error={t.erroresP.fecha} />
            <MontoField
              etiqueta={g.fee && !g.es_fee ? "Monto total (con fee)" : "Monto"}
              valor={t.formP.monto}
              onCambio={(v) => t.cambiarP("monto", v)}
              moneda={g.moneda}
              error={t.erroresP.monto}
              ayuda={`Falta pagar: ${formatMoneda(t.totalAPagar ?? g.saldo, g.moneda)}`}
            />
            {t.avisoFee ? (
              <p className="text-chico text-texto-suave">{`Va todo al proveedor. ${t.avisoFee}`}</p>
            ) : (
              g.fee && !g.es_fee && t.partesPago && (
                <p className="text-chico text-texto-suave">
                  {`Al proveedor ${formatMoneda(t.partesPago.proveedor, g.moneda)} · a la administración (fee) ${formatMoneda(t.partesPago.fee, g.moneda)}${g.tipo === "B" ? ", que se acredita solo a los fiduciarios." : ", que queda por cobrar hasta que se liquide en Honorarios."}`}
                </p>
              )
            )}
            {g.moneda === "ARS" && (
              <TextField etiqueta="Tipo de cambio (ARS por USD)" inputMode="decimal" ayuda="Se sugiere el último usado" value={t.formP.tipo_cambio} onChange={(e) => t.cambiarP("tipo_cambio", e.target.value)} error={t.erroresP.tipo_cambio} />
            )}
            <Select etiqueta="Medio de pago" opciones={t.opcionesMedio} valor={t.formP.medio} onCambio={(v) => t.cambiarP("medio", v as Pago["medio"])} />
            {t.porCuentaCorriente && (
              <Stack espacio={2}>
                <p className="text-chico text-texto-suave">
                  No sale plata de la Caja: lo que pusieron (de su bolsillo, o porque son el proveedor) se les acredita como un aporte a los que elijas, por su %, y el gasto queda pagado. Lo pagan igual todos los fiduciantes por su participación.
                </p>
                {t.formP.asignaciones.length > 0 && (
                  <DataList
                    etiqueta="A quién se le acredita"
                    columnas={columnasAsignacion}
                    filas={t.formP.asignaciones}
                    claveFila={(l) => l.clave}
                    acciones={(l) => (
                      <Button variante="fantasma" tamanio="sm" aria-label={`Quitar a ${l.nombre}`} onClick={() => t.quitarAsignacion(l.clave)}>
                        Quitar
                      </Button>
                    )}
                  />
                )}
                <Combobox etiqueta="Acreditar a" opciones={t.opcionesAsignacion} onElegir={t.agregarAsignacion} placeholder="Escribí el fiduciante o el grupo" />
                <Alerta texto={t.erroresP.asignaciones ?? null} />
              </Stack>
            )}
            {t.opcionesCajaPago && !t.porCuentaCorriente && <Select etiqueta="Caja" opciones={t.opcionesCajaPago} valor={t.formP.caja_id} onCambio={(v) => t.cambiarP("caja_id", v)} placeholder="Elegí la caja" />}
            <TextField etiqueta="Nota" ayuda="Opcional" value={t.formP.nota} onChange={(e) => t.cambiarP("nota", e.target.value)} error={t.erroresP.nota} />
            {d?.tipo === "pago" && !d.pago && (
              <Stack espacio={2}>
                <SelectorArchivos etiqueta="Comprobante del pago" textoBoton="Adjuntar comprobante" accept={ACCEPT_ADJUNTO} multiple onElegir={t.elegirComprobantes} />
                {t.comprobantes.map((a, i) => (
                  <Row key={`${a.name}-${i}`} justificar="entre" alinear="centro" espacio={2}>
                    <span className="truncate text-chico">{a.name}</span>
                    <Button variante="fantasma" tamanio="sm" aria-label={`Quitar ${a.name}`} onClick={() => t.quitarComprobante(i)}>
                      Quitar
                    </Button>
                  </Row>
                ))}
                <Alerta texto={t.errorComprobantes} />
              </Stack>
            )}
            <Alerta texto={t.errorPago} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardandoPago}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrarPago"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar pago">
        {d?.tipo === "borrarPago" && (
          <Stack espacio={4}>
            <p>{`¿Borrar el pago del ${formatFecha(d.pago.fecha)} por ${formatMoneda(d.pago.monto, d.pago.moneda)}? Sus adjuntos también se borran.`}</p>
            <Alerta texto={t.errorBorrarPago} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrandoPago} onClick={() => void t.confirmarBorrarPago()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
